import { CreateDialogFieldCompanion, useInCreateDialogSections } from 'components/create-dialog/CreateDialogRequired';
import { useDraftSaveMode } from 'components/drafts/DraftSaveMode';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import InputWrapper from 'components/forms/inputs/rhf-inputs/InputWrapper';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { InvoiceAutoGenerationScheduleReadonlyField } from 'components/InvoiceAutoGenerationScheduleInfo';
import { useModal } from 'components/modals/useModal';
import {
    MATTER_PRICELIST_EDITOR_MODAL_STYLE,
    MatterPricelistEditorModal,
    StagedPricelists,
} from 'pages/pricelists/modal/MatterPricelistEditorModal';
import { isPricingStaged, rateFigures } from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { clientPricelistsArg, findPricelistForMatter, MatterPricelistCard } from '../views/MatterPricelistCard';
import { skipToken } from '@reduxjs/toolkit/query';
import React, { useCallback, useMemo } from 'react';
import { Control, Controller, useFormState, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useFindPricelistsByClientQuery } from 'store/api/pricelists';
import { ClassifierData } from 'types/classifiers';
import { MatterForm, PricelistTabDraft, PricingLimitationType } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { PricelistCardEntry } from 'types/pricelists';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { buttonStyle } from 'components/buttons/buttonStyle';
import FeatureFlagged from 'components/featureflag/FeatureFlagged';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import InfoTooltip from 'components/InfoTooltip';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import { FeatureFlag } from 'types/features';
import { cn } from 'utils/cn';
import { MatterFormState, isPricingLocked } from './useMatterForm';
import { useStagedPricingLink } from './useStagedPricingLink';

/** Pricing: currency, price lists, fee arrangement, invoicing interval, discount and billing notes. */
export const PricingFields = ({ form }: { form: MatterFormState }): JSX.Element => {
    const { control, setValue } = form.methods;
    const {
        classifiers,
        currencyAccessible,
        currencyChangeNotice,
        endsFollowing,
        handleOnPaymentCurrencyCheckboxChange,
        handleOnPricelistCurrencyChange,
        hideDontNullifyOption,
        invoicingIntervalType,
        invoicingIntervalTypeOptions,
        isAdministrativeMatter,
        isLoadingNewClientData,
        isPaymentCurrencyDifferent,
        isUserPrivilegedToUpdateNonBillableHoursBehavior,
        matter,
        matterStateType,
        onFeeArrangementChange,
        onHourlyRatesChange,
        pricelistCurrencyType,
        pricingLimitationType,
        pricingLimitationTypeOptions,
        type,
    } = form;
    // On the record page the price list field names the stored list as the Pricing card does, from the read the
    // card already made.
    const inSection = useInRecordSection();
    const { data: clientPricelists } = useFindPricelistsByClientQuery(
        inSection ? clientPricelistsArg(matter) : skipToken
    );
    const storedPricelistName = (matter && findPricelistForMatter(clientPricelists, matter)?.name) ?? '';

    return (
        <>
            <SelectInput
                setValue={setValue}
                control={control}
                label="currency"
                placeholder="forms.select-currency"
                name="pricelistCurrencyType"
                onChange={handleOnPricelistCurrencyChange}
                options={
                    (getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                        label: string;
                        value: string;
                    }[]) ?? []
                }
                getOptionLabel="label"
                getOptionIdentifier="value"
                required
                disabled={(type === 'edit' && !currencyAccessible) || isPricingLocked(matterStateType?.value)}
            />
            <PriceListInput
                control={control}
                label="matters.pricelist"
                classifiers={classifiers}
                onChange={(value) => {
                    setValue('followsPricelist', value.followsPricelist, { shouldDirty: true });
                    onHourlyRatesChange(value);
                }}
                required={!isAdministrativeMatter}
                notice={currencyChangeNotice(pricelistCurrencyType?.value)}
                acceptsEmptyList={endsFollowing(pricelistCurrencyType?.value)}
                storedName={storedPricelistName}
                matterId={type === 'edit' ? matter?.id : undefined}
            />
            <FeatureFlagged action="show" flag={FeatureFlag.CASE_PAYMENT_CURRENCY_FIELD}>
                <React.Fragment key="payment-currency-on-matter">
                    {/* Ticked, it adds a required payment currency: the create dialog draws the two together. */}
                    <CreateDialogFieldCompanion field="paymentCurrencyType">
                        <CheckboxInput
                            name="isPaymentCurrencyDifferent"
                            control={control}
                            label={'matters.use-different-payment-currency'}
                            wrapperClassName="min-h-7 items-start"
                            onChange={handleOnPaymentCurrencyCheckboxChange}
                            rightLabelComponent={
                                <InfoTooltip
                                    // The margins space the icon from the checkbox's label. On the record page it
                                    // ends the label's line with no margin of its own, as the read label's icon
                                    // does: one would push it out of a line the label fills.
                                    className={inSection ? undefined : 'mt-[6px] ml-[6px]'}
                                    text="matters.tooltips.use-different-payment-currency"
                                />
                            }
                        />
                    </CreateDialogFieldCompanion>
                    {isPaymentCurrencyDifferent ? (
                        <SelectInput
                            setValue={setValue}
                            control={control}
                            label="payment-currency"
                            placeholder="forms.select-payment-currency"
                            name="paymentCurrencyType"
                            options={
                                (
                                    getValidOptionsFromClassifier(classifiers['CURRENCY']) as {
                                        label: string;
                                        value: string;
                                    }[]
                                ).filter((item) => item.value !== pricelistCurrencyType?.value) ?? []
                            }
                            getOptionLabel="label"
                            getOptionIdentifier="value"
                            required={isPaymentCurrencyDifferent}
                        />
                    ) : null}
                </React.Fragment>
            </FeatureFlagged>
            <SelectInput
                setValue={setValue}
                control={control}
                label="matters.fee-arrangement"
                placeholder="matters.select-fee-arrangement"
                name="pricingLimitationType"
                options={pricingLimitationTypeOptions}
                onChange={onFeeArrangementChange}
                getOptionLabel="label"
                getOptionIdentifier="value"
                required
                disabled={isLoadingNewClientData}
            />

            {/* isPricingCapped */}
            {pricingLimitationType?.value === PricingLimitationType.HOURLY_CAPPED ? (
                <>
                    <DecimalInput
                        control={control}
                        name="capLimit"
                        label="matters.cap-limit"
                        placeholder="matters.cap-limit"
                        required
                    />
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="matters.cap-unit"
                        name="capUnitType"
                        options={
                            (getValidOptionsFromClassifier(classifiers['CAP_UNIT_TYPE']).filter(
                                (option) => option.value == 'CASH'
                            ) as {
                                label: string;
                                value: string;
                            }[]) ?? []
                        }
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="matters.select-cap-unit"
                        required
                    />
                </>
            ) : null}

            {/* isPricingFixed */}
            {pricingLimitationType?.value === PricingLimitationType.FIXED_ONETIME ? (
                <DecimalInput
                    control={control}
                    name="fixedFee"
                    label="matters.fixed-fee-amount"
                    placeholder="matters.fixed-fee-amount"
                    required
                />
            ) : null}

            {/* isPricingNonBillable */}
            {pricingLimitationType?.value === PricingLimitationType.NON_BILLABLE && !hideDontNullifyOption ? (
                <CheckboxInput
                    control={control}
                    name="preserveNonBillableHours"
                    label="matters.preserve-non-billable-hours"
                    disabled={!isUserPrivilegedToUpdateNonBillableHoursBehavior}
                    rightLabelComponent={<InfoTooltip text={'matters.tooltips.preserve-non-billable-hours'} />}
                />
            ) : null}

            {/* isPricingRecurringFixed */}
            {pricingLimitationType?.value === PricingLimitationType.FIXED_RECURRING ? (
                <>
                    <DecimalInput
                        control={control}
                        name="recurringFixedFee"
                        label="matters.recurring-fee-amount"
                        placeholder="matters.recurring-fee-amount"
                        required
                    />
                </>
            ) : null}

            <SelectInput
                setValue={setValue}
                control={control}
                label="matters.invoicing-interval"
                placeholder="matters.select-invoicing-interval"
                name="invoicingIntervalType"
                options={invoicingIntervalTypeOptions}
                getOptionLabel="label"
                getOptionIdentifier="value"
                required={!isAdministrativeMatter}
                disabled={pricingLimitationType?.value === PricingLimitationType.PRO_BONO}
            />
            <InvoiceAutoGenerationScheduleReadonlyField invoicingIntervalCode={invoicingIntervalType?.value} />
            <PercentageInput
                control={control}
                name="discountPct"
                label="matters.discount"
                placeholder="matters.discount"
            />
            <DateInput control={control} name="discountExpiryDate" label="matters.discount-expiry" />
            <CheckboxInput control={control} name="isCostIncluded" label="matters.costs-included" />
            <TextAreaInput
                control={control}
                label="matters.billing-notes"
                placeholder="matters.billing-notes"
                name="billingNotes"
                enableAutofill
                tooltip={<InfoTooltip text="matters.billing-notes-tooltip" />}
            />
            <FeatureFlagged action="show" flag={FeatureFlag.INVOICING_INFO_ENABLED}>
                <TextAreaInput
                    name="invoicingInfo"
                    control={control}
                    label="matters.invoicing-info-internal"
                    placeholder="matters.invoicing-info-internal"
                    enableAutofill
                    tooltip={<InfoTooltip text="matters.invoicing-info-tooltip" />}
                />
            </FeatureFlagged>
        </>
    );
};

interface PropsPriceListInput {
    label: string;
    control: Control<MatterForm>;
    classifiers: Record<string, ClassifierData>;
    onChange: (value: StagedPricelists) => void;
    required?: boolean;
    // The save leaves the one empty list as it is, as after a follower's currency change.
    acceptsEmptyList?: boolean;
    notice?: string | null;
    /** The name of the case's stored list, shown over its figures until the lists are staged anew. */
    storedName?: string;
    /** A stored case's id: following a list in its place asks first, with the hours it re-prices. */
    matterId?: number;
}

const PriceListInput = ({
    control,
    label,
    classifiers,
    onChange,
    required = true,
    acceptsEmptyList = false,
    notice,
    storedName = '',
    matterId,
}: PropsPriceListInput) => {
    const isDraftSave = useDraftSaveMode();
    const { openModal, closeModal } = useModal();
    const inCreateDialog = useInCreateDialogSections();
    // On the record page the box is the Pricing card's cell, as tall as a field's and the read row's, its text
    // starting at the cell's edge where the read row has it.
    const inSection = useInRecordSection();
    const { dirtyFields } = useFormState({ control, name: ['pricelists', 'referencedPricelist'] });
    const [
        storedPricingKind,
        stagedFollowsPricelist,
        matterStateType,
        clientParty,
        referencedPricelist,
        pricelists,
        pricelistCurrencyType,
        serviceAgreementContract,
        openedDate,
        employees,
        administrators,
        paymentNotificationReceivers,
        responsibleEmployee,
        supervisedByEmployee,
    ] = useWatch({
        control,
        name: [
            'pricingKind',
            'followsPricelist',
            'matterStateType',
            'clientParty',
            'referencedPricelist',
            'pricelists',
            'pricelistCurrencyType',
            'serviceAgreementContract',
            'openedDate',
            'employees',
            'administrators',
            'paymentNotificationReceivers',
            'responsibleEmployee',
            'supervisedByEmployee',
        ],
    });
    const { pricingKind, followsPricelist } = useStagedPricingLink({
        isCreate: matterId === undefined,
        pricingKind: storedPricingKind,
        followsPricelist: stagedFollowsPricelist,
        pricelists,
        serviceAgreementContractId: serviceAgreementContract?.id,
    });
    // A non-administrative case needs a priced base list or a followed list. A draft does not.
    const rules = useMemo(
        () => ({
            validate: (tabs: PricelistTabDraft[]) =>
                !required ||
                isDraftSave ||
                acceptsEmptyList ||
                isPricingStaged(tabs, followsPricelist) ||
                t('matters.price-list-required'),
        }),
        [required, isDraftSave, acceptsEmptyList, followsPricelist]
    );
    const name = followsPricelist
        ? t('pricelists.case-follow.follows', { name: followsPricelist.name })
        : dirtyFields.pricelists || dirtyFields.referencedPricelist
          ? ''
          : storedName;

    // The team this save submits, responsible and supervising included: the ledger marks anyone priced outside it.
    const caseTeam = useMemo(() => {
        const people = new Map<number, NamedEntityMinDto>();
        [...employees, ...administrators, ...paymentNotificationReceivers, responsibleEmployee, supervisedByEmployee]
            .filter((person): person is NamedEntityMinDto => !!person)
            .forEach((person) => people.set(person.id, person));
        return [...people.values()];
    }, [employees, administrators, paymentNotificationReceivers, responsibleEmployee, supervisedByEmployee]);

    // The staged tabs have no stored figures yet, so the card computes them with the same formula.
    const cardLists = useMemo<PricelistCardEntry[]>(
        () => pricelists.map((tab) => ({ ...tab, ...rateFigures(tab.rates, tab.personalRates) })),
        [pricelists]
    );

    const openEditHourlyRatesModal = useCallback((): void => {
        openModal(
            <MatterPricelistEditorModal
                mode="form"
                clientParty={
                    clientParty
                        ? {
                              name: clientParty.name,
                              id: clientParty.id,
                          }
                        : undefined
                }
                matterId={matterId}
                referencedPricelist={referencedPricelist}
                pricingKind={pricingKind}
                followsPricelist={followsPricelist}
                tabs={pricelists}
                caseTeam={caseTeam}
                openedDate={openedDate}
                onClose={closeModal}
                onApply={(val) => {
                    onChange(val);
                    closeModal();
                }}
                classifiers={classifiers}
                currencyTypeCode={pricelistCurrencyType?.value}
                serviceAgreementContractId={serviceAgreementContract?.id}
            />,
            closeModal,
            MATTER_PRICELIST_EDITOR_MODAL_STYLE
        );
    }, [
        openModal,
        clientParty,
        matterId,
        referencedPricelist,
        pricingKind,
        followsPricelist,
        pricelists,
        caseTeam,
        openedDate,
        closeModal,
        classifiers,
        pricelistCurrencyType?.value,
        serviceAgreementContract?.id,
        onChange,
    ]);

    return (
        <Controller
            control={control}
            name={'pricelists'}
            rules={rules}
            render={({ fieldState: { error } }) => {
                const hasErrors = !!error;
                return (
                    <InputWrapper
                        name={'pricelists'}
                        label={label}
                        required={required}
                        isEmpty={!isPricingStaged(pricelists, followsPricelist)}
                        hasErrors={hasErrors}
                        error={hasErrors ? { message: error.message ?? t('matters.price-list-required') } : undefined}
                    >
                        <>
                            <div
                                className={cn(
                                    'bg-paper flex justify-between rounded-md border',
                                    inSection
                                        ? '-ml-[var(--field-hang,0px)] min-h-9 w-[calc(100%+var(--field-hang,0px))] items-start gap-2 py-[5px] pr-1.5 pl-2.5 text-sm font-medium'
                                        : 'pr-2.5 pl-3',
                                    // As high as the controls beside it in the create dialog: 36px, 44 on its sheet.
                                    !inSection && (inCreateDialog ? 'py-[5px] max-sm:py-[9px]' : 'py-2.5'),
                                    hasErrors ? 'border-destructive' : 'border-input'
                                )}
                            >
                                <div className={inSection ? 'min-w-0' : 'ml-0.5'}>
                                    <MatterPricelistCard
                                        name={name}
                                        oneLineName={inSection}
                                        currencyTypeCode={pricelistCurrencyType?.value}
                                        lists={cardLists}
                                        openedDate={openedDate}
                                        classifiers={classifiers}
                                        pricingKind={pricingKind}
                                        following={!!followsPricelist}
                                    />
                                </div>
                                <Button
                                    {...buttonStyle({ variant: 'outlined', size: 'extraSmall' })}
                                    disabled={isPricingLocked(matterStateType?.value)}
                                    onClick={() => openEditHourlyRatesModal()}
                                >
                                    <FormattedMessage id="edit" />
                                </Button>
                            </div>
                            {notice ? <div className="text-warning-dark mt-1 text-xs">{notice}</div> : null}
                        </>
                    </InputWrapper>
                );
            }}
        />
    );
};
